import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { regexValidator } from 'src/app/helpers/customFormValidators';
import { DataService } from 'src/app/services/data/data.service';
import { LocationUIService } from 'src/app/services/location-ui/location-ui.service';
import { LocationService } from 'src/app/services/location/location.service';
import { MapService } from 'src/app/services/map/map.service';
import { GenericTypeTemplateService } from 'src/app/services/generic-type-template/generic-type-template.service';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.sass'],
})
export class EditComponent implements OnInit, OnDestroy, AfterViewInit {
  public form: FormGroup;
  parentSelection = [];
  parentSelected;
  id;
  unsubber: Subject<boolean> = new Subject<boolean>();

  isGetCoor: boolean;
  currZoomLevel: number = 0;
  genericTemplates = [];

  @ViewChild('formContainer') formContainer: ElementRef;

  get formCoor() {
    return this.form.controls['coordinate'];
  }
  get formName() {
    return this.form.controls['name'];
  }
  get formDescription() {
    return this.form.controls['description'];
  }

  /** This is a variable to be assigned with disable map click function */
  _disableMapClick: () => void;

  locAdditionalData = [];

  constructor(
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private router: Router,
    private activatedRouter: ActivatedRoute,
    private location: Location,
    private locationService: LocationService,
    private toastr: ToastrService,
    private mapService: MapService,
    private genericTemplatesService: GenericTypeTemplateService,
    private _t: TranslateService,
    private locationUIService: LocationUIService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      coordinate: new FormControl('', [
        Validators.required,
        regexValidator(
          /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?),\s*[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/,
          'coordinateFormat'
        ),
      ]),
      address: '',
      status: '',
      parent: '',
      parentName: '',
      visibility: ['', Validators.required],
      default_zoom_level: '',
      generic_type_template: '',
      ctqrs_enabled: false,
      faq_enabled: false,
      show_annotation_marker: false,
      show_review_module: false,
      additional_data_key: '',
      additional_data_val: '',
    });
    this.activatedRouter.parent.params
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((params) => {
        if (params['id']) {
          this.id = params['id'];
          this.locationService
            .findOne(this.id)
            .pipe(takeUntil(this.unsubber.asObservable()))
            .subscribe((res) => {
              this.form.patchValue({
                name: res.name,
                description: res.description,
                coordinate: `${res.coordinate[0]}, ${res.coordinate[1]}`,
                address: res.address,
                status: res.status,
                visibility: res.visibility,
                default_zoom_level: res.default_zoom_level,
                generic_type_template: res.generic_type_template?.id || '',
                ctqrs_enabled: res.ctqrs_enabled,
                faq_enabled: res.faq_enabled,
                show_annotation_marker: res.show_annotation_marker,
                show_review_module: res.show_review_module,
              });
              if (res.parent && res.parent.id) {
                this.form.patchValue({
                  parent: res.parent.id,
                  parentName: res.parent.name,
                });
              }
              if (res.additional_data_json) {
                this.locAdditionalData = JSON.parse(res.additional_data_json);
              }
            });
        }
      });

    this.genericTemplatesService
      .findAll()
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        if (res?.data) {
          this.genericTemplates = res.data;
        }
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const map = this.mapService.map;
      this.currZoomLevel = map.getZoom().toFixed(3);
      map.on('move', (e) => {
        this.currZoomLevel = map.getZoom().toFixed(3);
      });
    });
  }

  ngOnDestroy() {
    this.unsubber.next();
    this.unsubber.unsubscribe();
  }

  onSubmit() {
    if (this.form.valid) {
      const form = this.form.value;
      // if (this.parentSelected?.id) {
      //   form.parent = this.parentSelected.id
      // } else {
      //   // delete form.parent;
      //   form.parent = null
      // }
      // form['coordinate'] = [this.form.value['longitude'], this.form.value['latitude']]
      // if (form.parent.length < 1) {form.parent = null}
      for (var propName in form) {
        if (
          form[propName] === null ||
          form[propName] === undefined ||
          form[propName] === ''
        ) {
          delete form[propName];
        }
      }
      form['coordinate'] = (
        this.form.controls['coordinate'].value as string
      ).split(', ');
      form['additional_data_json'] = JSON.stringify(this.locAdditionalData);
      this.spinner.show();
      this.locationService
        .update(this.id, form)
        .pipe(takeUntil(this.unsubber.asObservable()))
        .subscribe(
          (res) => {
            this.spinner.hide();
            if (res.status === 'inactive') {
              if (res.parent) {
                this.router.navigate([
                  'projects',
                  res.parent,
                  'details',
                  'summary',
                ]);
              } else {
                this.router.navigate(['/projects']);
              }
            } else {
              this.locationUIService.refreshCurrLocation(this.id);
              this.locationUIService.refreshDefectCounts(this.id);
              this.router.navigate(['projects', this.id, 'details', 'summary']);
            }
          },
          ({ error }) => {
            const msg = error.message
              .reduce((results, u) => ((results += u + ', '), results), '')
              .slice(0, -2);
            this.toastr.error(this._t.instant(msg), this._t.instant('Error'));
          }
        );
    }
  }

  onDiscard() {
    if (this.location.getState() === 1) {
      this.router.navigate(['/']);
    } else {
      this.location.back();
    }
  }

  searchParent($event) {
    if ($event.target.value.length > 0) {
      this.locationService
        .findAll([
          { key: 'limit', val: 5 },
          { key: 'filtered[0][key]', val: 'name' },
          { key: 'filtered[0][val]', val: $event.target.value },
          { key: 'filtered[0][type]', val: 'contains' },
          { key: 'limit', val: 3 },
        ])
        .pipe(takeUntil(this.unsubber.asObservable()))
        .subscribe((res) => {
          this.parentSelection = res.data;
        });
    } else {
      this.form.patchValue({
        parent: undefined,
        parentName: '',
      });
      this.parentSelection = [];
    }
  }

  selectedParent(parent) {
    this.parentSelected = parent;
    this.form.patchValue({
      parent: this.parentSelected.id,
      parentName: this.parentSelected.name,
    });
    this.parentSelection = [];
  }

  /**
   * Retrieves map Lnglat and fill the related fields
   */
  getMapLngLat() {
    const unsub = new Subject();

    this.toggleCoordinate();

    const res = this.mapService.enableMapCLick();
    const { click$, disableFn } = res;

    this._disableMapClick = disableFn;

    click$.pipe(takeUntil(unsub.asObservable())).subscribe((evt) => {
      const { lngLat } = evt;

      if (!lngLat) return;

      this.form.patchValue({
        coordinate: lngLat.lng.toFixed(6) + ', ' + lngLat.lat.toFixed(6),
      });
      this.disableMapClick();

      unsub.next();
      unsub.unsubscribe();
    });
  }

  /** Disables map click with the function provided from mapService.enableMapClick */
  disableMapClick() {
    this._disableMapClick();
    this.toggleCoordinate();
  }

  /**
   * Toggles the coordinate input state, together with its button
   */
  toggleCoordinate() {
    const control = this.form.controls['coordinate'];

    if (control.enabled) {
      this.isGetCoor = true;
      control.disable();
    } else {
      this.isGetCoor = false;
      control.enable();
    }
  }

  setDefaultZoom(value): void {
    this.form.patchValue({
      default_zoom_level: value,
    });
  }

  onToggleChange(control) {
    this.form.controls[control].setValue(!this.form.get(control).value);
  }

  addAD() {
    const locAdditionalDatum = {
      key: this.form.controls.additional_data_key.value,
      value: this.form.controls.additional_data_val.value,
    };

    this.locAdditionalData.push(locAdditionalDatum);

    this.form.controls.additional_data_key.setValue('');
    this.form.controls.additional_data_val.setValue('');

    try {
      setTimeout(() => {
        this.formContainer.nativeElement.scrollTop =
          this.formContainer.nativeElement.scrollHeight;
      }, 100);
    } catch (err) {}
  }

  removeAD(idx) {
    this.locAdditionalData.splice(idx, 1);
  }
}
